import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
} from '@dnd-kit/core';
import { Clock, User2, GripVertical, AlertCircle } from 'lucide-react';
import { apiClient, type Lead } from '../lib/api.js';
import { Badge } from '../components/ui/Badge.js';
import {
  cn,
  formatBRL,
  formatRelative,
  stageColor,
  stageLabel,
  temperatureColor,
  temperatureLabel,
} from '../lib/utils.js';

// Stages visíveis no Kanban (visual order)
const VISIBLE_STAGES = [
  'identified',
  'checkout_started',
  'payment_pending',
  'customer_front',
  'upsell_decision',
  'customer_pro',
  'active_customer',
  'at_risk',
  'won',
] as const;

export function Pipeline(): JSX.Element {
  const queryClient = useQueryClient();
  const [optimisticMove, setOptimisticMove] = useState<{ leadId: string; toStage: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pipeline'],
    queryFn: () => apiClient.pipeline(),
    refetchInterval: 30_000,
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) => apiClient.moveLeadStage(id, stage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipeline'] });
      setOptimisticMove(null);
    },
    onError: () => setOptimisticMove(null),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const leadId = String(active.id);
    const toStage = String(over.id);
    setOptimisticMove({ leadId, toStage });
    moveMutation.mutate({ id: leadId, stage: toStage });
  };

  // Aplicar movimento otimista
  const getLeadsByStage = (stage: string): Lead[] => {
    if (!data) return [];
    const original = data.byStage[stage] ?? [];
    if (!optimisticMove) return original;
    if (optimisticMove.toStage === stage) {
      // Adicionar o lead aqui se não está
      const movedLead = Object.values(data.byStage).flat().find((l) => l.id === optimisticMove.leadId);
      if (movedLead && !original.find((l) => l.id === movedLead.id)) {
        return [{ ...movedLead, stage }, ...original];
      }
    }
    // Remover lead que saiu
    return original.filter((l) => l.id !== optimisticMove.leadId);
  };

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Carregando pipeline...</div>;
  }

  return (
    <div className="h-screen flex flex-col">
      <header className="px-6 py-4 border-b border-border">
        <h1 className="text-2xl font-bold tracking-tight">Pipeline</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {data?.total ?? 0} leads ativos · Arraste cards entre colunas para mudar de stage
        </p>
      </header>

      <div className="flex-1 overflow-x-auto overflow-y-hidden p-4">
        <DndContext sensors={sensors} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
          <div className="flex gap-3 h-full">
            {VISIBLE_STAGES.map((stage) => {
              const leads = getLeadsByStage(stage);
              return <Column key={stage} stage={stage} leads={leads} />;
            })}
          </div>
        </DndContext>
      </div>
    </div>
  );
}

function Column({ stage, leads }: { stage: string; leads: Lead[] }): JSX.Element {
  const { isOver, setNodeRef } = useDroppable({ id: stage });
  const totalValue = leads.reduce((sum, l) => sum + Number(l.lastOrderCents ?? 0), 0);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'w-72 shrink-0 flex flex-col rounded-lg border bg-card/50 transition',
        isOver ? 'border-primary bg-primary/5' : 'border-border',
      )}
    >
      <div className="px-3 py-2 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge className={cn(stageColor[stage] ?? '', 'text-[11px]')}>{stageLabel[stage] ?? stage}</Badge>
          <span className="text-xs text-muted-foreground">{leads.length}</span>
        </div>
        {totalValue > 0 && (
          <span className="text-[11px] font-mono text-muted-foreground">{formatBRL(totalValue)}</span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {leads.length === 0 ? (
          <div className="text-center text-xs text-muted-foreground py-6">Vazio</div>
        ) : (
          leads.map((lead) => <LeadCard key={lead.id} lead={lead} />)
        )}
      </div>
    </div>
  );
}

function LeadCard({ lead }: { lead: Lead }): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id });
  const probPurchase = lead.scores?.prob_purchase_24h;

  return (
    <div
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={cn(
        'group rounded-md border border-border bg-background p-2.5 cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-50 shadow-lg ring-2 ring-primary',
      )}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <Link
            to={`/contatos/${lead.contactId}`}
            className="font-medium text-sm hover:underline truncate block"
            onClick={(e) => e.stopPropagation()}
          >
            {lead.contactName ?? 'Sem nome'}
          </Link>
          <div className="text-[11px] text-muted-foreground truncate">{lead.contactEmail}</div>

          <div className="mt-2 flex flex-wrap items-center gap-1">
            <Badge className={cn(temperatureColor[lead.temperature] ?? '', 'text-[10px]')}>
              {temperatureLabel[lead.temperature]}
            </Badge>
            {probPurchase !== undefined && Number(probPurchase) > 0.5 && (
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">
                {(Number(probPurchase) * 100).toFixed(0)}%
              </Badge>
            )}
          </div>

          {lead.nextAction && (
            <div className="mt-2 flex items-start gap-1 text-[11px] text-amber-400/90">
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{lead.nextAction}</span>
            </div>
          )}

          <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
            {lead.ownerName && (
              <span className="inline-flex items-center gap-1">
                <User2 className="h-2.5 w-2.5" /> {lead.ownerName.split(' ')[0]}
              </span>
            )}
            <span className="inline-flex items-center gap-1 ml-auto">
              <Clock className="h-2.5 w-2.5" /> {formatRelative(lead.stageChangedAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
